/* 0.46.64 - Elements on one line are aligned by their MAIN PART.
 *
 * The main part of an element is what it is: the input box of a field
 * (not its label, error or hint above, nor its summary below), the
 * text of a title, the card, the image, the button. A Column group is
 * one main part as a whole.
 *
 * Per line of a row, every element is shifted down (a top margin) so
 * that, by its Vertical position on the page (data-valign on its
 * column, or the row's data-align-default):
 *
 *   top     its main part's top lines up with the others' (default)
 *   center  its main part is centred on the tallest main part's height
 *   bottom  its main part's bottom lines up with the tallest's
 *
 * So two fields side by side line up their boxes whatever their labels
 * and hints, and a button beside a tall card can sit at its middle.
 *
 * Rows: the page grid's .row (bands, Column groups) and any container
 * marked data-align-row (a Repeating display's lines, a Line group).
 * Inner rows are done before outer ones, since aligning inside a group
 * can change its height.
 *
 * A field marks its input with two zero-height markers,
 * [data-main-start] and [data-main-end], or a component marks its main
 * part with [data-main]. Anything else is measured whole (its content,
 * without its own outer margins).
 */

import fieldStyles from '../components/Input.module.css';

const CONTAINERS = '.row, [data-align-row]';
// The parts of a field that are not its main part: the label, the hint,
// the summary and an error message (Input.module.css, shared by the
// page, the preview and the Input component).
const NOT_MAIN = [fieldStyles.label, fieldStyles.help, fieldStyles.summary, 'field-error-text']
  .filter(Boolean);
const isNotMain = (el) => NOT_MAIN.some(c => el.classList && el.classList.contains(c));

function visible(el) {
  if (!el || el.nodeType !== 1) return false;
  const r = el.getBoundingClientRect();
  // 0.46.87 - Not the zero-height row break placed before an element
  // with a Horizontal position on the page: it shared a line with the
  // element after it and was pushed down by that element's label
  // (28px more space above every centered field).
  if (r.height === 0) return false;
  return r.width > 0;
}

/* The element's main part: { top, height } in viewport pixels. */
function mainOf(item) {
  // A group (a Column group, a nested row): the whole of it.
  if (!item.querySelector(CONTAINERS)) {
    // A component that marks its main part itself (a file upload's
    // button row).
    const marked = item.querySelector('[data-main]');
    if (marked) {
      const r = marked.getBoundingClientRect();
      if (r.height > 0) return { top: r.top, height: r.height };
    }
    const s = item.querySelector('[data-main-start]');
    const e = item.querySelector('[data-main-end]');
    if (s && e) {
      const top = s.getBoundingClientRect().top;
      const bottom = e.getBoundingClientRect().top;
      if (bottom > top) return { top, height: bottom - top };
    }
    // A field without the marks (the preview, the Input component):
    // its parts other than the label, hint, summary and error.
    const field = fieldStyles.field && item.querySelector(`.${fieldStyles.field}`);
    if (field) {
      let top = Infinity;
      let bottom = -Infinity;
      for (const ch of field.children) {
        if (isNotMain(ch)) continue;
        const r = ch.getBoundingClientRect();
        if (r.height <= 0) continue;
        top = Math.min(top, r.top);
        bottom = Math.max(bottom, r.bottom);
      }
      if (Number.isFinite(top)) return { top, height: bottom - top };
    }
  }
  // Its content's box, without the column's own margins: the union of
  // its visible children (a title's text, a card), or the item itself.
  let top = Infinity;
  let bottom = -Infinity;
  for (const ch of item.children) {
    const r = ch.getBoundingClientRect();
    if (r.height <= 0) continue;
    top = Math.min(top, r.top);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!Number.isFinite(top)) {
    const r = item.getBoundingClientRect();
    return { top: r.top, height: r.height };
  }
  return { top, height: bottom - top };
}

function alignContainer(c) {
  const def = c.getAttribute('data-align-default') || 'top';
  const items = [...c.children].filter(visible);
  if (items.length < 2) return;
  // Lines: items sharing a top (after the reset, a line's items start
  // together, the row aligning them at its top).
  const lines = [];
  for (const it of items) {
    const top = it.getBoundingClientRect().top;
    let line = lines.find(l => Math.abs(l.top - top) < 2);
    if (!line) { line = { top, items: [] }; lines.push(line); }
    line.items.push(it);
  }
  for (const line of lines) {
    if (line.items.length < 2) continue;
    const data = line.items.map(it => {
      const m = mainOf(it);
      const va = it.getAttribute('data-valign') || def;
      return { it, mt: m.top - line.top, h: m.height, va };
    });
    const H = Math.max(...data.map(d => d.h));
    for (const d of data) {
      d.d = d.va === 'center' ? (H - d.h) / 2 : d.va === 'bottom' ? H - d.h : 0;
    }
    // The common top: low enough that no element moves up.
    const T = Math.max(...data.map(d => d.mt - d.d));
    for (const d of data) {
      const off = Math.round(T + d.d - d.mt);
      if (off > 0) {
        d.it.style.marginTop = `${off}px`;
        d.it.setAttribute('data-va-off', String(off));
      }
    }
  }
}

export function alignMainParts(root) {
  if (!root) return;
  const containers = [...root.querySelectorAll(CONTAINERS)];
  // Undo the last pass first: positions are measured as laid out.
  for (const c of containers) {
    for (const it of c.children) {
      if (it.hasAttribute && it.hasAttribute('data-va-off')) {
        it.style.marginTop = '';
        it.removeAttribute('data-va-off');
      }
    }
  }
  // Inner rows first (a descendant comes after its ancestor in
  // document order, so the reverse order visits it first).
  for (const c of containers.reverse()) alignContainer(c);
}

/* Keeps a root aligned: after any change of its content or size. Our
 * own margins are attributes and style, which are not watched, so a
 * pass does not trigger the next one. Returns a stop function. */
export function watchMainAlign(root) {
  if (!root || typeof window === 'undefined') return () => {};
  let frame = 0;
  const run = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => { frame = 0; alignMainParts(root); });
  };
  run();
  const mo = new MutationObserver(run);
  mo.observe(root, { childList: true, subtree: true, characterData: true });
  let ro = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(run);
    ro.observe(root);
  }
  window.addEventListener('resize', run);
  // Pictures change heights when they arrive.
  root.addEventListener('load', run, true);
  return () => {
    mo.disconnect();
    if (ro) ro.disconnect();
    window.removeEventListener('resize', run);
    root.removeEventListener('load', run, true);
    if (frame) window.cancelAnimationFrame(frame);
  };
}
